import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Form, Select, Space, Switch, Typography } from 'antd';
import aiAnalysisService from '../../../services/aiAnalysis.service';
import type { AiArtifactType, AiProviderOption } from '../../../types/ai-analysis.types';

const artifactOptions = [
  { value: 'DETAILED_ANALYSIS', label: 'Análise detalhada' },
  { value: 'SUMMARY_ANALYSIS', label: 'Análise resumida' },
  { value: 'ASSOCIATIVE_IMAGE', label: 'Imagem associativa 1:1' },
];

export default function AiGenerationForm({
  surveyId,
  onCreated,
}: {
  surveyId: string;
  onCreated?: () => void;
}) {
  const [catalog, setCatalog] = useState<AiProviderOption[]>([]);
  const [artifacts, setArtifacts] = useState<AiArtifactType[]>(['DETAILED_ANALYSIS']);
  const [models, setModels] = useState<Record<string, string>>({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [external, setExternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    aiAnalysisService.getCatalog().then(setCatalog);
  }, []);
  const optionsFor = useMemo(
    () => (type: AiArtifactType) =>
      catalog.flatMap((provider) =>
        provider.models
          .filter((model) =>
            type === 'ASSOCIATIVE_IMAGE'
              ? model.capabilities.rasterImage
              : model.capabilities.structuredText
          )
          .map((model) => ({
            value: model.id,
            label: `${provider.displayName} — ${model.displayName}`,
          }))
      ),
    [catalog]
  );
  const submit = async () => {
    setSubmitting(true);
    try {
      await aiAnalysisService.createRequest(surveyId, {
        selections: artifacts.map((artifactType) => ({
          artifactType,
          modelId: models[artifactType],
        })),
        externalContextEnabled: external,
        microdataProcessingAcknowledged: acknowledged,
      });
      onCreated?.();
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Form layout="vertical">
      <Alert
        type="warning"
        showIcon
        message="Processamento de microdados"
        description="A IA receberá todas as respostas finais e perfis registrados, incluindo cidade e respostas abertas. Identificadores técnicos e cadastrais são removidos; a saída exige revisão humana."
      />
      <Form.Item label="Conteúdos" style={{ marginTop: 16 }}>
        <Checkbox.Group
          options={artifactOptions}
          value={artifacts}
          onChange={(values) => setArtifacts(values as AiArtifactType[])}
        />
      </Form.Item>
      {artifacts.map((type) => (
        <Form.Item
          key={type}
          label={`Provedor e modelo — ${artifactOptions.find((o) => o.value === type)?.label}`}
          required
        >
          <Select
            value={models[type]}
            options={optionsFor(type)}
            onChange={(value) => setModels((current) => ({ ...current, [type]: value }))}
          />
        </Form.Item>
      ))}
      <Space direction="vertical">
        <Space>
          <Switch checked={external} onChange={setExternal} />
          <Typography.Text>Permitir contexto externo com fontes</Typography.Text>
        </Space>
        <Checkbox
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
        >
          Estou ciente e autorizo o envio dos microdados pseudonimizados ao provedor selecionado.
        </Checkbox>
        <Button
          type="primary"
          loading={submitting}
          disabled={!acknowledged || !artifacts.length || artifacts.some((type) => !models[type])}
          onClick={submit}
        >
          Gerar conteúdos
        </Button>
      </Space>
    </Form>
  );
}
